import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ActivityLog, ActivityFilter } from '../types/activity.types';
import { Notification } from '../types/notification.types';
import { getRoleInfo, RoleInfo } from '../utils/roleUtils';

// Types
export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'admin' | 'user' | 'superadmin';
  organizationId?: string;
  token?: string;
}

export interface CartItem {
  productId: string;
  productName: string;
  description: string;
  brandName: string;
  category: string;
  offerPrice: number;
  quantity: number;
  totalPrice: number;
}

export interface AppState {
  // User state
  user: User | null;
  roleInfo: RoleInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Cart state
  cartItems: CartItem[];
  cartLength: number;

  // UI state
  sidebarOpen: boolean;
  theme: 'light' | 'dark';

  // Toast notifications (temporary UI messages)
  toastNotifications: Array<{
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
    timestamp: number;
  }>;

  // Real-time notifications
  notifications: Notification[];
  unreadNotificationCount: number;

  // Activity logs
  activities: ActivityLog[];
  activityFilter: ActivityFilter;

  // Data refresh
  refreshTrigger: number;
}

export interface AppActions {
  // User actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;

  // Cart actions
  addToCart: (item: CartItem) => void;
  removeFromCart: (productId: string) => void;
  updateCartQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;

  // UI actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setTheme: (theme: 'light' | 'dark') => void;

  // Toast notification actions
  addToastNotification: (notification: Omit<AppState['toastNotifications'][0], 'id' | 'timestamp'>) => void;
  removeToastNotification: (id: string) => void;
  clearToastNotifications: () => void;

  // Real-time notification actions
  setNotifications: (notifications: Notification[]) => void;
  addNotification: (notification: Notification) => void;
  markNotificationAsRead: (notificationId: string) => void;
  markAllNotificationsAsRead: () => void;
  removeNotification: (notificationId: string) => void;
  updateUnreadCount: (count: number) => void;

  // Activity actions
  setActivities: (activities: ActivityLog[]) => void;
  addActivity: (activity: ActivityLog) => void;
  setActivityFilter: (filter: Partial<ActivityFilter>) => void;
  clearActivities: () => void;

  // Data refresh
  triggerRefresh: () => void;
}

export type AppStore = AppState & AppActions;

// Create the store
export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      roleInfo: null,
      isAuthenticated: false,
      isLoading: false,
      cartItems: [],
      cartLength: 0,
      sidebarOpen: true,
      theme: 'light',
      toastNotifications: [],
      notifications: [],
      unreadNotificationCount: 0,
      activities: [],
      activityFilter: {
        type: null,
        dateFrom: null,
        dateTo: null,
        userId: null,
      },
      refreshTrigger: 0,

      // User actions
      setUser: (user) => {
        set({
          user,
          roleInfo: getRoleInfo(user),
          isAuthenticated: !!user,
        });
        // Store token in sessionStorage for API calls
        if (user?.token) {
          sessionStorage.setItem('token', user.token);
          sessionStorage.setItem('user', JSON.stringify(user));
        }
      },

      setLoading: (isLoading) => set({ isLoading }),

      logout: () => {
        set({
          user: null,
          roleInfo: null,
          isAuthenticated: false,
          cartItems: [],
          cartLength: 0,
          notifications: [],
          unreadNotificationCount: 0,
          activities: [],
        });
        // Clear sessionStorage
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
      },

      // Cart actions
      addToCart: (item) => {
        const { cartItems } = get();
        const existingItem = cartItems.find(cartItem => cartItem.productId === item.productId);
        
        if (existingItem) {
          // Update quantity if item exists
          const updatedCart = cartItems.map(cartItem =>
            cartItem.productId === item.productId
              ? { ...cartItem, quantity: cartItem.quantity + item.quantity, totalPrice: (cartItem.quantity + item.quantity) * cartItem.offerPrice }
              : cartItem
          );
          set({
            cartItems: updatedCart,
            cartLength: updatedCart.reduce((sum, item) => sum + item.quantity, 0),
          });
        } else {
          // Add new item
          const updatedCart = [...cartItems, item];
          set({
            cartItems: updatedCart,
            cartLength: updatedCart.reduce((sum, item) => sum + item.quantity, 0),
          });
        }
      },

      removeFromCart: (productId) => {
        const { cartItems } = get();
        const updatedCart = cartItems.filter(item => item.productId !== productId);
        set({
          cartItems: updatedCart,
          cartLength: updatedCart.reduce((sum, item) => sum + item.quantity, 0),
        });
      },

      updateCartQuantity: (productId, quantity) => {
        const { cartItems } = get();
        if (quantity <= 0) {
          get().removeFromCart(productId);
          return;
        }
        
        const updatedCart = cartItems.map(item =>
          item.productId === productId
            ? { ...item, quantity, totalPrice: quantity * item.offerPrice }
            : item
        );
        set({
          cartItems: updatedCart,
          cartLength: updatedCart.reduce((sum, item) => sum + item.quantity, 0),
        });
      },

      clearCart: () => {
        set({
          cartItems: [],
          cartLength: 0,
        });
      },

      // UI actions
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),

      setTheme: (theme) => set({ theme }),

      // Toast notification actions
      addToastNotification: (notification) => {
        const id = Date.now().toString();
        const newNotification = {
          ...notification,
          id,
          timestamp: Date.now(),
        };
        set((state) => ({
          toastNotifications: [...state.toastNotifications, newNotification],
        }));
      },

      removeToastNotification: (id) => {
        set((state) => ({
          toastNotifications: state.toastNotifications.filter(n => n.id !== id),
        }));
      },

      clearToastNotifications: () => set({ toastNotifications: [] }),

      // Real-time notification actions
      setNotifications: (notifications) => {
        const unreadCount = notifications.filter(n => !n.isRead).length;
        set({ notifications, unreadNotificationCount: unreadCount });
      },

      addNotification: (notification) => {
        set((state) => {
          const updatedNotifications = [notification, ...state.notifications];
          const unreadCount = updatedNotifications.filter(n => !n.isRead).length;
          return {
            notifications: updatedNotifications,
            unreadNotificationCount: unreadCount,
          };
        });
      },

      markNotificationAsRead: (notificationId) => {
        set((state) => {
          const updatedNotifications = state.notifications.map(n =>
            n._id === notificationId ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
          );
          const unreadCount = updatedNotifications.filter(n => !n.isRead).length;
          return {
            notifications: updatedNotifications,
            unreadNotificationCount: unreadCount,
          };
        });
      },

      markAllNotificationsAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map(n => ({
            ...n,
            isRead: true,
            readAt: n.readAt || new Date().toISOString(),
          })),
          unreadNotificationCount: 0,
        }));
      },

      removeNotification: (notificationId) => {
        set((state) => {
          const updatedNotifications = state.notifications.filter(n => n._id !== notificationId);
          const unreadCount = updatedNotifications.filter(n => !n.isRead).length;
          return {
            notifications: updatedNotifications,
            unreadNotificationCount: unreadCount,
          };
        });
      },

      updateUnreadCount: (count) => {
        set({ unreadNotificationCount: count });
      },

      // Activity actions
      setActivities: (activities) => set({ activities }),

      addActivity: (activity) => {
        set((state) => ({
          activities: [activity, ...state.activities],
        }));
      },

      setActivityFilter: (filter) => {
        set((state) => ({
          activityFilter: { ...state.activityFilter, ...filter },
        }));
      },

      clearActivities: () => set({ activities: [] }),

      // Data refresh
      triggerRefresh: () => set((state) => ({ refreshTrigger: state.refreshTrigger + 1 })),
    }),
    {
      name: 'app-store',
      partialize: (state) => ({
        user: state.user,
        roleInfo: state.roleInfo,
        isAuthenticated: state.isAuthenticated,
        cartItems: state.cartItems,
        cartLength: state.cartLength,
        theme: state.theme,
      }),
    }
  )
);

// Selectors for common use cases
export const useUser = () => useAppStore((state) => state.user);
export const useRoleInfo = () => useAppStore((state) => state.roleInfo);
export const useIsAuthenticated = () => useAppStore((state) => state.isAuthenticated);
export const useCart = () => useAppStore((state) => ({
  items: state.cartItems,
  length: state.cartLength,
  total: state.cartItems.reduce((sum, item) => sum + item.totalPrice, 0),
}));
export const useUI = () => useAppStore((state) => ({
  sidebarOpen: state.sidebarOpen,
  theme: state.theme,
  isLoading: state.isLoading,
}));
export const useToastNotifications = () => useAppStore((state) => state.toastNotifications);
export const useNotifications = () => useAppStore((state) => ({
  notifications: state.notifications,
  unreadCount: state.unreadNotificationCount,
}));
export const useActivities = () => useAppStore((state) => state.activities);
export const useActivityFilter = () => useAppStore((state) => state.activityFilter);

export default useAppStore;
